import { useMemo } from "react";

/**
 * Renderizador de Markdown seguro, optimizado y sin dependencias externas
 * para el Creative Director de VEYRA (Etapa 2B.4A).
 *
 * Características:
 * - 100% libre de dangerouslySetInnerHTML (sin riesgo de inyección XSS).
 * - Encabezados H1, H2, H3 visualmente diferenciados con acentos ámbar VEYRA.
 * - Negritas, cursivas y negrita-cursiva.
 * - Listas ordenadas (1., 2.) y no ordenadas (- o *) con viñetas temáticas.
 * - Bloques de código con cabecera de lenguaje y overflow horizontal.
 * - Código en línea con estilo mono ámbar.
 * - Resaltado sintáctico de handles cinematográficos (@personaje, @Escena, etc.).
 * - Separación de párrafos y tipografía diseñada para lectura prolongada.
 * - Ajuste responsivo de palabras largas con break-words y overflow-wrap.
 */

// Parser de tokens en línea
const parseInlineTokens = (text) => {
  if (!text) return [];

  // Expresión regular para capturar en orden de precedencia:
  // 1. Código en línea: `...`
  // 2. Negrita + Cursiva: ***...*** o ___...___
  // 3. Negrita: **...** o __...__
  // 4. Cursiva: *...* o _..._
  // 5. Enlaces markdown: [label](url)
  // 6. URLs crudas: https?://...
  // 7. Handles: @\w+
  const regex = /(`[^`]+`|\*\*\*.*?\*\*\*|___.*?___|\*\*.*?\*\*|__.*?__|\*[^*]+?\*|_[^_]+?_|\[[^\]]+\]\(https?:\/\/[^\s)]+\)|https?:\/\/[^\s)]+|@[\wáéíóúÁÉÍÓÚñÑ]+)/g;

  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", content: text.slice(lastIndex, match.index) });
    }

    const token = match[0];
    if (token.startsWith("`") && token.endsWith("`") && token.length >= 2) {
      parts.push({ type: "code", content: token.slice(1, -1) });
    } else if (
      (token.startsWith("***") && token.endsWith("***") && token.length >= 6) ||
      (token.startsWith("___") && token.endsWith("___") && token.length >= 6)
    ) {
      parts.push({ type: "bold_italic", content: token.slice(3, -3) });
    } else if (
      (token.startsWith("**") && token.endsWith("**") && token.length >= 4) ||
      (token.startsWith("__") && token.endsWith("__") && token.length >= 4)
    ) {
      parts.push({ type: "bold", content: token.slice(2, -2) });
    } else if (
      (token.startsWith("*") && token.endsWith("*") && token.length >= 2) ||
      (token.startsWith("_") && token.endsWith("_") && token.length >= 2)
    ) {
      parts.push({ type: "italic", content: token.slice(1, -1) });
    } else if (token.startsWith("[") && token.includes("](") && token.endsWith(")")) {
      const linkMatch = token.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);
      if (linkMatch) {
        parts.push({ type: "link", label: linkMatch[1], url: linkMatch[2] });
      } else {
        parts.push({ type: "text", content: token });
      }
    } else if (token.startsWith("http://") || token.startsWith("https://")) {
      parts.push({ type: "link", label: token, url: token });
    } else if (token.startsWith("@")) {
      parts.push({ type: "handle", handle: token });
    } else {
      parts.push({ type: "text", content: token });
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push({ type: "text", content: text.slice(lastIndex) });
  }

  return parts;
};

// Renderizado de tokens en línea como elementos React tipados
const renderInline = (text) => {
  const tokens = parseInlineTokens(text);
  return tokens.map((token, idx) => {
    switch (token.type) {
      case "bold_italic":
        return (
          <strong key={idx} className="font-semibold italic text-[#f4f1ec]">
            {token.content}
          </strong>
        );
      case "bold":
        return (
          <strong key={idx} className="font-semibold text-[#f4f1ec]">
            {token.content}
          </strong>
        );
      case "italic":
        return (
          <em key={idx} className="italic text-[#d8d4cd]">
            {token.content}
          </em>
        );
      case "code":
        return (
          <code
            key={idx}
            className="rounded bg-[#202020] px-1 py-0.5 font-mono text-[10.5px] text-[#f5b544] border border-[#333]"
          >
            {token.content}
          </code>
        );
      case "handle":
        return (
          <span
            key={idx}
            className="inline-flex items-center rounded bg-[#f5b544]/10 border border-[#f5b544]/25 px-1 py-0.2 font-mono text-[10.5px] font-medium text-[#f5b544]"
          >
            {token.handle}
          </span>
        );
      case "link":
        return (
          <a
            key={idx}
            href={token.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#f5b544] underline decoration-[#f5b544]/40 underline-offset-2 hover:decoration-[#f5b544] break-all transition-colors"
          >
            {token.label}
          </a>
        );
      default:
        return token.content;
    }
  });
};

// Parser de bloques Markdown
const parseMarkdownBlocks = (text) => {
  if (!text) return [];
  const lines = text.split(/\r?\n/);
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // 1. Bloques de código delimitados por ``` (soporta streaming abierto)
    if (line.trim().startsWith("```")) {
      const lang = line.trim().slice(3).trim();
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        codeLines.push(lines[i]);
        i++;
      }
      if (i < lines.length && lines[i].trim().startsWith("```")) {
        i++; // saltar delimitador de cierre
      }
      blocks.push({ type: "code", lang, content: codeLines.join("\n") });
      continue;
    }

    // 2. Regla horizontal
    if (/^(---|___|\*\*\*)$/.test(line.trim())) {
      blocks.push({ type: "hr" });
      i++;
      continue;
    }

    // 3. Encabezados (#, ##, ###, ####)
    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      blocks.push({
        type: "heading",
        level: headingMatch[1].length,
        content: headingMatch[2].trim(),
      });
      i++;
      continue;
    }

    // 4. Bloques de cita (blockquotes >)
    if (line.trim().startsWith(">")) {
      const quoteLines = [line.trim().replace(/^>\s?/, "")];
      i++;
      while (i < lines.length && lines[i].trim().startsWith(">")) {
        quoteLines.push(lines[i].trim().replace(/^>\s?/, ""));
        i++;
      }
      blocks.push({ type: "blockquote", content: quoteLines.join("\n") });
      continue;
    }

    // 5. Listas con viñetas (- o *)
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, "").trim());
        i++;
      }
      blocks.push({ type: "ul", items });
      continue;
    }

    // 6. Listas numeradas (1. , 2. )
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, "").trim());
        i++;
      }
      blocks.push({ type: "ol", items });
      continue;
    }

    // 7. Línea vacía
    if (!line.trim()) {
      i++;
      continue;
    }

    // 8. Párrafo ordinario
    const paraLines = [line.trim()];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !lines[i].trim().startsWith("```") &&
      !lines[i].trim().startsWith("#") &&
      !lines[i].trim().startsWith(">") &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !/^(---|___|\*\*\*)$/.test(lines[i].trim())
    ) {
      paraLines.push(lines[i].trim());
      i++;
    }
    blocks.push({ type: "paragraph", content: paraLines.join(" ") });
  }

  return blocks;
};

export const MarkdownContent = ({ content = "", className = "" }) => {
  const blocks = useMemo(() => parseMarkdownBlocks(content), [content]);

  if (!content) return null;

  return (
    <div
      className={`markdown-content flex flex-col gap-1.5 text-xs leading-relaxed text-[#d8d4cd] break-words [overflow-wrap:anywhere] ${className}`}
    >
      {blocks.map((block, idx) => {
        switch (block.type) {
          case "heading": {
            if (block.level === 1) {
              return (
                <h1
                  key={idx}
                  className="mt-3 mb-1.5 border-b border-[#303030] pb-1 text-sm font-bold tracking-wide text-[#f5b544]"
                >
                  {renderInline(block.content)}
                </h1>
              );
            }
            if (block.level === 2) {
              return (
                <h2
                  key={idx}
                  className="mt-2.5 mb-1 text-xs font-semibold tracking-wide text-[#f4f1ec]"
                >
                  {renderInline(block.content)}
                </h2>
              );
            }
            if (block.level === 3) {
              return (
                <h3
                  key={idx}
                  className="mt-2 mb-0.5 text-[11px] font-semibold uppercase tracking-wider text-[#a3a3a3]"
                >
                  {renderInline(block.content)}
                </h3>
              );
            }
            return (
              <h4
                key={idx}
                className="mt-1.5 mb-0.5 text-[11px] font-medium text-[#c0bcb5]"
              >
                {renderInline(block.content)}
              </h4>
            );
          }

          case "paragraph":
            return (
              <p key={idx} className="my-0.5 leading-relaxed text-[#d8d4cd]">
                {renderInline(block.content)}
              </p>
            );

          case "ul":
            return (
              <ul
                key={idx}
                className="my-1 space-y-1 pl-4 list-disc marker:text-[#f5b544]/70 text-[#d8d4cd]"
              >
                {block.items.map((item, itemIdx) => (
                  <li key={itemIdx} className="pl-0.5 leading-relaxed">
                    {renderInline(item)}
                  </li>
                ))}
              </ul>
            );

          case "ol":
            return (
              <ol
                key={idx}
                className="my-1 space-y-1 pl-4 list-decimal marker:text-[#f5b544]/80 text-[#d8d4cd]"
              >
                {block.items.map((item, itemIdx) => (
                  <li key={itemIdx} className="pl-0.5 leading-relaxed">
                    {renderInline(item)}
                  </li>
                ))}
              </ol>
            );

          case "code":
            return (
              <div
                key={idx}
                className="my-2 overflow-hidden rounded-lg border border-[#303030] bg-[#101010] shadow-inner"
              >
                {block.lang && (
                  <div className="flex items-center justify-between border-b border-[#252525] bg-[#141414] px-3 py-1 font-mono text-[10px] text-[#888]">
                    <span>{block.lang}</span>
                  </div>
                )}
                <pre className="overflow-x-auto p-3 font-mono text-[11px] text-[#e5e5e5] leading-relaxed whitespace-pre">
                  <code>{block.content}</code>
                </pre>
              </div>
            );

          case "blockquote":
            return (
              <blockquote
                key={idx}
                className="my-1.5 border-l-2 border-[#f5b544]/60 bg-[#141414] pl-3 py-1 text-xs italic text-[#b5b0a7]"
              >
                {renderInline(block.content)}
              </blockquote>
            );

          case "hr":
            return <hr key={idx} className="my-2 border-t border-[#2e2e2e]" />;

          default:
            return null;
        }
      })}
    </div>
  );
};

export default MarkdownContent;
